import { App as AntApp, Modal } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button, Card, PageHeader } from "../components/ui";
import { errorMessage, post, put, safeGet } from "../lib/api";
import { downloadText, fmtDate, int, isNum, nextQuarterStart } from "../lib/format";
import { useTheme } from "../theme/ThemeContext";
import { ACCENTS, THEMES } from "../theme/tokens";

const QW = [
  ["QW_CONSISTENCY", "Consistency"],
  ["QW_GROWTH", "Growth"],
  ["QW_MARGIN", "Margin"],
  ["QW_ROE", "ROE"],
  ["QW_DEBT", "Debt"],
  ["QW_WEALTH", "Wealth"],
  ["QW_RECENT", "Recent"],
];
const NOTIFY = [
  ["NOTIFY_SCAN_SUMMARY", "Scan start & summary"],
  ["NOTIFY_TOP_PER_MARKET", "Top 10 per market"],
  ["NOTIFY_REBALANCE", "Rebalance plan (buy / sell / keep)"],
  ["NOTIFY_ONLY_NEW_BUY", "Only when a new BUY appears"],
  ["NOTIFY_ERRORS", "Errors"],
];
const MARKETS = [
  { key: "US", label: "US — S&P 500", note: "503 · SPY" },
  { key: "INDIA", label: "India — Nifty 50", note: "50 · ^NSEI" },
  { key: "CHINA", label: "China — Hang Seng", note: "29 · ^HSI" },
];

const EMPTY = { values: {}, defaults: {}, overrides: [], secrets: {} };

function same(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function NumInput({ id, value, onChange, step = 1, min, max, suffix, prefix, width }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, width }}>
      {prefix && <span style={{ fontSize: 12, color: "var(--muted)" }}>{prefix}</span>}
      <input
        id={id}
        type="number"
        className="input num"
        step={step}
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      />
      {suffix && <span style={{ fontSize: 12, color: "var(--muted)" }}>{suffix}</span>}
    </div>
  );
}

function ThemeCard({ name, checked, onSelect }) {
  const t = THEMES[name];
  return (
    <button type="button" role="radio" aria-checked={checked} className="theme-radio" onClick={onSelect}>
      <span style={{ display: "flex", gap: 4 }}>
        <span className="swatch" style={{ background: t.bg, border: `1px solid ${t.border}` }} />
        <span className="swatch" style={{ background: name === "Dark" ? t.surface : t.sidebar, border: name === "Dark" ? `1px solid ${t.border}` : "none" }} />
        <span className="swatch" style={{ background: "var(--accent)" }} />
      </span>
      {name}
    </button>
  );
}

export default function Settings() {
  const { message } = AntApp.useApp();
  const { status, me } = useOutletContext() || {};
  const theme = useTheme();
  const [data, setData] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [offline, setOffline] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [holdings, setHoldings] = useState(null);

  useEffect(() => {
    (async () => {
      const d = await safeGet("/api/settings", null);
      if (d && d.values) {
        setData({ ...EMPTY, ...d });
        setDraft(d.values);
      } else {
        setOffline(true);
      }
      setLoaded(true);
      const ps = await Promise.all(["US", "INDIA", "CHINA"].map((m) => safeGet(`/api/portfolio/${m}`, null)));
      let n = 0;
      let asOf = null;
      let any = false;
      ps.forEach((p) => {
        if (p) {
          any = true;
          n += p.holdings?.length || 0;
          if (p.as_of && (!asOf || p.as_of > asOf)) asOf = p.as_of;
        }
      });
      setHoldings(any ? { n, asOf } : null);
    })();
  }, []);

  const v = draft;
  const set = (k, val) => setDraft((d) => ({ ...d, [k]: val }));
  const changed = useMemo(() => Object.keys(v).filter((k) => !same(v[k], data.values[k])), [v, data.values]);

  const qwSum = QW.reduce((s, [k]) => s + (isNum(v[k]) ? v[k] : 0), 0);
  const qwOk = Math.abs(qwSum - 1) < 0.005;

  const save = async () => {
    if (!changed.length) return;
    if (!qwOk) return message.error("Quality weights must sum to 1.00");
    setSaving(true);
    try {
      const values = {};
      changed.forEach((k) => {
        values[k] = v[k];
      });
      const res = await put("/api/settings", { values });
      if (res?.values) {
        setData({ ...EMPTY, ...res });
        setDraft(res.values);
      } else {
        setData((d) => ({ ...d, values: { ...d.values, ...values } }));
      }
      message.success(`Saved ${changed.length} setting${changed.length === 1 ? "" : "s"} — applies to the next scan`);
    } catch (e) {
      message.error(errorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
    return null;
  };

  const resetDefaults = () => {
    if (!Object.keys(data.defaults || {}).length) return message.info("Defaults unavailable");
    setDraft({ ...v, ...data.defaults });
    message.info("Reset to defaults — not saved yet");
    return null;
  };

  const action = async (name, label) => {
    setBusy(name);
    try {
      const res = await post(`/api/actions/${name}`, {});
      message.success(res?.message || `${label} done`);
    } catch (e) {
      message.error(errorMessage(e, `${label} failed`));
    } finally {
      setBusy("");
    }
  };

  const forceRebalance = () => {
    Modal.confirm({
      title: "Force a rebalance now?",
      content: "This recomputes the equal-weight target for every market on the next scan and may generate buy and sell orders outside the quarterly schedule.",
      okText: "Force rebalance",
      okButtonProps: { danger: true },
      onOk: () => action("force_rebalance", "Force rebalance"),
    });
  };

  const exportEnv = () => {
    const snapshot = { exported_at: new Date().toISOString(), overrides: data.overrides, values: v };
    downloadText(`undr_settings_${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(snapshot, null, 2), "application/json");
  };

  const nq = nextQuarterStart(holdings?.asOf || new Date());
  const nextRb = `${nq.date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · Q${nq.quarter}${holdings ? ` · ${holdings.n} holdings` : ""}`;
  const mode = status?.mode || me?.mode;
  const markets = Array.isArray(v.MARKETS) ? v.MARKETS : [];
  const toggleMarket = (m) => set("MARKETS", markets.includes(m) ? markets.filter((x) => x !== m) : [...markets, m]);
  const disabled = !loaded || offline;

  return (
    <>
      <PageHeader title="Settings" sub="Scoring, buy rules, portfolio, schedule and notifications. Changes apply to the next scan.">
        {changed.length > 0 && (
          <span style={{ fontSize: 12, color: "var(--warn)", display: "flex", alignItems: "center", gap: 6 }}>
            <span className="dot" style={{ background: "var(--warn)" }} />
            {changed.length} unsaved change{changed.length === 1 ? "" : "s"}
          </span>
        )}
        {offline && <span style={{ fontSize: 12, color: "var(--muted)" }}>Backend unreachable — settings read-only</span>}
        <Button onClick={resetDefaults} disabled={disabled}>Reset to defaults</Button>
        <Button variant="primary" onClick={save} disabled={disabled || saving || !changed.length || !qwOk}>
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </PageHeader>

      <div className="settings-grid">
        {/* Quality weights */}
        <Card id="s-q" title="Quality score weights" desc={
          <>
            Seven dimensions, must sum to 1.00 · currently{" "}
            <span className="num" style={{ color: qwOk ? "var(--good)" : "var(--bad)", fontWeight: 600 }}>{qwSum.toFixed(2)}</span>
          </>
        }>
          {QW.map(([k, label]) => (
            <div className="w" key={k}>
              <label htmlFor={k}>{label}</label>
              <input id={k} type="range" min={0} max={0.5} step={0.05} value={isNum(v[k]) ? v[k] : 0} onChange={(e) => set(k, Number(e.target.value))} disabled={disabled} />
              <span className="num">{isNum(v[k]) ? v[k].toFixed(2) : "—"}</span>
            </div>
          ))}
          <div className="row">
            <div className="field"><label htmlFor="REV_CAGR_FULL">Revenue CAGR for full score</label><NumInput id="REV_CAGR_FULL" value={v.REV_CAGR_FULL} onChange={(x) => set("REV_CAGR_FULL", x)} suffix="%" /></div>
            <div className="field"><label htmlFor="ROE_FULL">ROE for full score</label><NumInput id="ROE_FULL" value={v.ROE_FULL} onChange={(x) => set("ROE_FULL", x)} suffix="%" /></div>
          </div>
          <div className="row">
            <div className="field"><label htmlFor="OP_MARGIN_FULL">Op. margin for full score</label><NumInput id="OP_MARGIN_FULL" value={v.OP_MARGIN_FULL} onChange={(x) => set("OP_MARGIN_FULL", x)} suffix="%" /></div>
            <div className="field"><label htmlFor="NET_DEBT_EQ_MAX">Net debt / equity → 0</label><NumInput id="NET_DEBT_EQ_MAX" step={0.1} value={v.NET_DEBT_EQ_MAX} onChange={(x) => set("NET_DEBT_EQ_MAX", x)} /></div>
          </div>
          <div className="field">
            <label>Min years (US / IN / CN)</label>
            <div className="row">
              <NumInput value={v.QUALITY_MIN_YEARS_US} onChange={(x) => set("QUALITY_MIN_YEARS_US", x)} />
              <NumInput value={v.QUALITY_MIN_YEARS_INDIA} onChange={(x) => set("QUALITY_MIN_YEARS_INDIA", x)} />
              <NumInput value={v.QUALITY_MIN_YEARS_CHINA} onChange={(x) => set("QUALITY_MIN_YEARS_CHINA", x)} />
            </div>
          </div>
        </Card>

        {/* Fair value & buy rules */}
        <Card id="s-fv" title="Fair value & buy rules" desc={'"Recover your money in N years" from owner earnings, plus net cash'}>
          <div className="row">
            <div className="field"><label htmlFor="FV_YEARS">Payback years</label><NumInput id="FV_YEARS" value={v.FV_YEARS} onChange={(x) => set("FV_YEARS", x)} /></div>
            <div className="field">
              <label htmlFor="FV_DISCOUNT">Discount rate</label>
              <NumInput id="FV_DISCOUNT" step={0.5} value={v.FV_DISCOUNT} onChange={(x) => set("FV_DISCOUNT", x)} suffix="%" />
              <span className="hint">0 = pure payback (Jitta Line)</span>
            </div>
          </div>
          <div className="row">
            <div className="field"><label htmlFor="FV_GROWTH_MAX">Growth cap</label><NumInput id="FV_GROWTH_MAX" step={0.5} value={v.FV_GROWTH_MAX} onChange={(x) => set("FV_GROWTH_MAX", x)} suffix="%" /></div>
            <div className="field"><label htmlFor="FV_GROWTH_HAIRCUT">Growth haircut</label><NumInput id="FV_GROWTH_HAIRCUT" step={0.05} value={v.FV_GROWTH_HAIRCUT} onChange={(x) => set("FV_GROWTH_HAIRCUT", x)} prefix="×" /></div>
          </div>
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "24px minmax(0, 1fr)", gap: 10, alignItems: "start" }}>
              <span className="rule-badge" style={{ background: "var(--goodSoft)", color: "var(--goodInk)" }}>B</span>
              <div style={{ fontSize: 13, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                Quality ≥
                <input type="number" step={0.1} className="input inline num" aria-label="Strong quality threshold" value={v.BUY_QUALITY_STRONG ?? ""} onChange={(e) => set("BUY_QUALITY_STRONG", Number(e.target.value))} />
                and price ≤ <span className="num">1.00</span> × fair
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "24px minmax(0, 1fr)", gap: 10, alignItems: "start" }}>
              <span className="rule-badge" style={{ background: "var(--goodSoft)", color: "var(--goodInk)" }}>B</span>
              <div style={{ fontSize: 13, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                Quality ≥
                <input type="number" step={0.1} className="input inline num" aria-label="Minimum quality threshold" value={v.BUY_QUALITY_MIN ?? ""} onChange={(e) => set("BUY_QUALITY_MIN", Number(e.target.value))} />
                and price ≤
                <input type="number" step={0.05} className="input inline num" aria-label="Price to fair for mid quality" value={v.BUY_DISCOUNT_MID ?? ""} onChange={(e) => set("BUY_DISCOUNT_MID", Number(e.target.value))} />
                × fair
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "24px minmax(0, 1fr)", gap: 10, alignItems: "center" }}>
              <span className="rule-badge" style={{ background: "var(--warnSoft)", color: "var(--warnInk)" }}>W</span>
              <span style={{ fontSize: 13, color: "var(--muted)" }}>
                Quality ≥ {isNum(v.BUY_QUALITY_MIN) ? v.BUY_QUALITY_MIN : 5} but price above the rule → WATCH; below → AVOID
              </span>
            </div>
          </div>
          <div className="field">
            <label>Rank weights (Q / value / momentum)</label>
            <div className="row">
              <NumInput step={0.05} value={v.RANK_W_QUALITY} onChange={(x) => set("RANK_W_QUALITY", x)} />
              <NumInput step={0.05} value={v.RANK_W_VALUE} onChange={(x) => set("RANK_W_VALUE", x)} />
              <NumInput step={0.05} value={v.RANK_W_MOMENTUM} onChange={(x) => set("RANK_W_MOMENTUM", x)} />
            </div>
          </div>
          <div className="row">
            <div className="field" style={{ maxWidth: 140 }}><label htmlFor="TIMING_WEIGHT">Timing weight</label><NumInput id="TIMING_WEIGHT" step={0.05} value={v.TIMING_WEIGHT} onChange={(x) => set("TIMING_WEIGHT", x)} /></div>
          </div>
        </Card>

        {/* Portfolio */}
        <Card id="s-p" title="Portfolio & rebalance" desc="Equal weight, quarterly, holdings survive inside the keep band">
          <div className="row">
            <div className="field"><label htmlFor="PORTFOLIO_TOP_N_US">Top N — US</label><NumInput id="PORTFOLIO_TOP_N_US" value={v.PORTFOLIO_TOP_N_US} onChange={(x) => set("PORTFOLIO_TOP_N_US", x)} /></div>
            <div className="field"><label htmlFor="PORTFOLIO_TOP_N_INDIA">India</label><NumInput id="PORTFOLIO_TOP_N_INDIA" value={v.PORTFOLIO_TOP_N_INDIA} onChange={(x) => set("PORTFOLIO_TOP_N_INDIA", x)} /></div>
            <div className="field"><label htmlFor="PORTFOLIO_TOP_N_CHINA">China</label><NumInput id="PORTFOLIO_TOP_N_CHINA" value={v.PORTFOLIO_TOP_N_CHINA} onChange={(x) => set("PORTFOLIO_TOP_N_CHINA", x)} /></div>
          </div>
          <div className="row">
            <div className="field"><label htmlFor="PORTFOLIO_MAX_PER_SECTOR">Max per sector</label><NumInput id="PORTFOLIO_MAX_PER_SECTOR" value={v.PORTFOLIO_MAX_PER_SECTOR} onChange={(x) => set("PORTFOLIO_MAX_PER_SECTOR", x)} /></div>
            <div className="field"><label htmlFor="PORTFOLIO_KEEP_BAND">Keep band (× N)</label><NumInput id="PORTFOLIO_KEEP_BAND" step={0.1} value={v.PORTFOLIO_KEEP_BAND} onChange={(x) => set("PORTFOLIO_KEEP_BAND", x)} /></div>
          </div>
          <div className="field">
            <label className="muted">Rebalance</label>
            <select className="select" disabled aria-label="Rebalance schedule">
              <option>Every calendar quarter (after first scan of the quarter)</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "var(--bg)", borderRadius: 8, gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Next rebalance</span>
              <span className="num" style={{ fontSize: 12, color: "var(--muted)" }}>{nextRb}</span>
            </div>
            <Button size="sm" onClick={forceRebalance} disabled={busy === "force_rebalance"}>Force now…</Button>
          </div>
          <div><h2 style={{ fontSize: 14 }}>Screening</h2></div>
          <div className="row">
            <div className="field"><label htmlFor="MIN_QUALITY">Min quality shown</label><NumInput id="MIN_QUALITY" step={0.5} value={v.MIN_QUALITY} onChange={(x) => set("MIN_QUALITY", x)} /></div>
            <div className="field"><label htmlFor="MAX_STOCKS_PER_SECTOR">Max per sector (leaderboard)</label><NumInput id="MAX_STOCKS_PER_SECTOR" value={v.MAX_STOCKS_PER_SECTOR} onChange={(x) => set("MAX_STOCKS_PER_SECTOR", x)} /></div>
          </div>
        </Card>

        {/* Markets & schedule */}
        <Card id="s-m" title="Markets & schedule" desc="Universe from Wikipedia, refreshed every 7 days">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {MARKETS.map((m) => (
              <label key={m.key} className="check boxed">
                <span>
                  <input type="checkbox" checked={markets.includes(m.key)} onChange={() => toggleMarket(m.key)} disabled={disabled} />
                  {m.label}
                </span>
                <span className="num" style={{ color: "var(--muted)", fontSize: 12 }}>{m.note}</span>
              </label>
            ))}
          </div>
          <div className="row">
            <div className="field">
              <label htmlFor="SCAN_TIME_ET">Scan time (ET, weekdays)</label>
              <input id="SCAN_TIME_ET" type="time" className="input num" value={v.SCAN_TIME_ET || ""} onChange={(e) => set("SCAN_TIME_ET", e.target.value)} />
            </div>
            <div className="field"><label htmlFor="FUNDAMENTALS_MAX_AGE_DAYS">Fundamentals cache max age</label><NumInput id="FUNDAMENTALS_MAX_AGE_DAYS" value={v.FUNDAMENTALS_MAX_AGE_DAYS} onChange={(x) => set("FUNDAMENTALS_MAX_AGE_DAYS", x)} suffix="days" /></div>
          </div>
          <div className="row">
            <div className="field"><label htmlFor="STATEMENT_REFRESH_LAG_DAYS">Statement refresh lag</label><NumInput id="STATEMENT_REFRESH_LAG_DAYS" value={v.STATEMENT_REFRESH_LAG_DAYS} onChange={(x) => set("STATEMENT_REFRESH_LAG_DAYS", x)} suffix="days" /></div>
            <div className="field"><label htmlFor="SCAN_SLEEP">Sleep between requests</label><NumInput id="SCAN_SLEEP" step={0.1} value={v.SCAN_SLEEP} onChange={(x) => set("SCAN_SLEEP", x)} suffix="s" /></div>
          </div>
          <div className="field">
            <label className="muted">Statement lag (US / IN / CN days)</label>
            <div className="row">
              <NumInput value={v.FUNDAMENTAL_LAG_DAYS_US} onChange={(x) => set("FUNDAMENTAL_LAG_DAYS_US", x)} />
              <NumInput value={v.FUNDAMENTAL_LAG_DAYS_INDIA} onChange={(x) => set("FUNDAMENTAL_LAG_DAYS_INDIA", x)} />
              <NumInput value={v.FUNDAMENTAL_LAG_DAYS_CHINA} onChange={(x) => set("FUNDAMENTAL_LAG_DAYS_CHINA", x)} />
            </div>
            <span className="hint">Point-in-time guard for backtests</span>
          </div>
        </Card>

        {/* Notifications */}
        <Card id="s-n" title="Notifications" desc="Telegram bot · messages are HTML, top 10 per market">
          <div className="field">
            <label className="muted" htmlFor="bot-token">Bot token</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input id="bot-token" type="password" className="input" readOnly value={data.secrets?.telegram_configured ? "••••••••••••••••••••••••" : ""} placeholder={loaded ? "Not configured" : ""} style={{ flexGrow: 1 }} />
              <a className="btn" style={{ minHeight: 40, padding: "0 12px", fontSize: 12 }} href="https://docs.github.com/en/actions/security-for-github-actions/security-guides/using-secrets-in-github-actions" target="_blank" rel="noreferrer">Rotate</a>
            </div>
            <span className="hint">Stored as a GitHub secret, never in the repo</span>
          </div>
          <div className="field">
            <label className="muted" htmlFor="chat-id">Chat ID</label>
            <input id="chat-id" type="text" className="input num" readOnly value={data.secrets?.chat_id_masked || ""} placeholder={loaded ? "Not configured" : ""} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {NOTIFY.map(([k, label]) => (
              <label key={k} className="check">
                <input type="checkbox" checked={!!v[k]} onChange={(e) => set(k, e.target.checked)} disabled={disabled} />
                {label}
              </label>
            ))}
          </div>
          <Button style={{ alignSelf: "flex-start" }} onClick={() => action("send_test_message", "Test message")} disabled={busy === "send_test_message"}>
            {busy === "send_test_message" ? "Sending…" : "Send test message"}
          </Button>
        </Card>

        {/* Appearance */}
        <Card id="s-a" title="Appearance" desc="Theme and accent colour for the dashboard — saved per browser">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "var(--muted)" }}>Theme</span>
            <div role="radiogroup" aria-label="Theme" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
              {Object.keys(THEMES).map((name) => (
                <ThemeCard key={name} name={name} checked={theme.theme === name} onSelect={() => theme.setTheme(name)} />
              ))}
            </div>
            {theme.followSystem && theme.effectiveTheme !== theme.theme && (
              <span className="hint">Following system: showing {theme.effectiveTheme}</span>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "var(--muted)" }}>Accent colour</span>
            <div role="radiogroup" aria-label="Accent colour" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {ACCENTS.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  role="radio"
                  aria-checked={theme.accent.toLowerCase() === a.value.toLowerCase()}
                  aria-label={a.name}
                  className="accent-radio"
                  style={{ background: a.value }}
                  onClick={() => theme.setAccent(a.value)}
                />
              ))}
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--muted)", marginLeft: 6 }}>
                Custom
                <input
                  type="color"
                  value={theme.accent}
                  onChange={(e) => theme.setAccent(e.target.value)}
                  style={{ width: 36, height: 36, border: "1px solid var(--border)", borderRadius: 8, padding: 2, background: "var(--surface)" }}
                />
              </label>
            </div>
          </div>
          <label className="check">
            <input type="checkbox" checked={theme.followSystem} onChange={(e) => theme.setFollowSystem(e.target.checked)} />
            Follow system light / dark
          </label>
          <label className="check">
            <input type="checkbox" checked={theme.colorRows} onChange={(e) => theme.setColorRows(e.target.checked)} />
            Colour-code BUY / WATCH / AVOID rows
          </label>
        </Card>

        {/* Cloud & data */}
        <Card id="s-c" title="Cloud & data" desc="Scan on GitHub Actions, results in Cloudflare R2, dashboard on Pages">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="status-row">
              <span><span className="dot" style={{ background: status ? "var(--good)" : "var(--muted)" }} />{mode === "cloud" ? "GitHub Actions" : mode === "local" ? "Local Flask server" : "Backend"}</span>
              <span className="num">{status ? (status.next_run ? `next ${fmtDate(status.next_run)}` : status.state) : "offline"}</span>
            </div>
            <div className="status-row">
              <span><span className="dot" style={{ background: status ? "var(--good)" : "var(--muted)" }} />{mode === "cloud" ? "R2 bucket" : "data/ folder"}</span>
              <span className="num">{status?.last_scan ? `last scan ${fmtDate(status.last_scan)}` : "—"}</span>
            </div>
            <div className="status-row">
              <span><span className="dot" style={{ background: status ? "var(--good)" : "var(--muted)" }} />{mode === "cloud" ? "Pages functions" : "API"}</span>
              <span className="num">/api/* · {me?.email ? `${me.admin ? "admin" : "viewer"} · ${me.email}` : mode || "—"}</span>
            </div>
            <div className="status-row">
              <span><span className="dot" style={{ background: status?.state === "error" ? "var(--warn)" : status ? "var(--good)" : "var(--muted)" }} />Scanner</span>
              <span className="num">{status?.state === "error" ? status.message || "error" : status?.state || "—"}{data.overrides?.length ? ` · ${int(data.overrides.length)} overrides` : ""}</span>
            </div>
          </div>
          <div className="row">
            <Button onClick={() => action("clear_cache", "Clear cache")} disabled={busy === "clear_cache"}>Clear fundamentals cache</Button>
            <Button onClick={() => action("refresh_universe", "Refresh universe")} disabled={busy === "refresh_universe"}>Refresh universe</Button>
          </div>
          <div className="field">
            <label className="muted">Export</label>
            <div className="row">
              <Button onClick={exportEnv} disabled={!loaded}>.env snapshot</Button>
              <a className="btn" href="/api/portfolio/US" download="holdings_US.json" aria-disabled={!status}>holdings_*.json</a>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
